"use client";

import type { RefObject } from "react";
import { useEffect, useRef } from "react";
import type { EditorMediaReference } from "./adapters";
import type { AudioMix } from "./model";

export const DEFAULT_AUDIO_MIX: AudioMix = {
  musicVolume: 1,
  vocalsVolume: 0.2,
};

export type StemMixMediaReference = EditorMediaReference & {
  vocalStemUrl: string;
  instrumentalStemUrl: string;
};

export function clampAudioMixVolume(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function normalizeAudioMix(value: AudioMix | null | undefined): AudioMix | null {
  if (!value) return null;
  return {
    musicVolume: clampAudioMixVolume(value.musicVolume),
    vocalsVolume: clampAudioMixVolume(value.vocalsVolume),
  };
}

export function mediaHasStemMix(
  media: EditorMediaReference | null
): media is StemMixMediaReference {
  return (
    typeof media?.vocalStemUrl === "string" &&
    media.vocalStemUrl.length > 0 &&
    typeof media.instrumentalStemUrl === "string" &&
    media.instrumentalStemUrl.length > 0
  );
}

export function defaultAudioMixForMedia(media: EditorMediaReference | null): AudioMix | null {
  return mediaHasStemMix(media) ? DEFAULT_AUDIO_MIX : null;
}

export function normalizeAudioMixForMedia(
  media: EditorMediaReference | null,
  audioMix: AudioMix | null | undefined
): AudioMix | null {
  if (!mediaHasStemMix(media)) return null;
  return normalizeAudioMix(audioMix) ?? DEFAULT_AUDIO_MIX;
}

function createAudioContext(): AudioContext | null {
  const AudioContextCtor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return AudioContextCtor ? new AudioContextCtor() : null;
}

interface StemAudioGraph {
  context: AudioContext;
  musicElement: HTMLAudioElement;
  vocalsElement: HTMLAudioElement;
  musicGain: GainNode;
  vocalsGain: GainNode;
  masterGain: GainNode;
}

function createStemElement(url: string): HTMLAudioElement {
  const element = new Audio(url);
  element.crossOrigin = "anonymous";
  element.preload = "auto";
  return element;
}

function syncElement(element: HTMLMediaElement, controller: HTMLMediaElement) {
  try {
    if (Number.isFinite(controller.currentTime)) {
      element.currentTime = controller.currentTime;
    }
    element.playbackRate = controller.playbackRate;
  } catch {
    // The stem element may not be seekable until its metadata has arrived.
  }
}

export function useStemAudioMixPreview({
  media,
  audioMix,
  previewRef,
  volume,
  muted,
}: {
  media: EditorMediaReference | null;
  audioMix: AudioMix | null;
  previewRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  volume: number;
  muted: boolean;
}) {
  const graphRef = useRef<StemAudioGraph | null>(null);
  const mix = normalizeAudioMixForMedia(media, audioMix);
  const mixActive = Boolean(mix);
  const stemKey = mediaHasStemMix(media)
    ? `${media.instrumentalStemUrl}\n${media.vocalStemUrl}`
    : null;

  useEffect(() => {
    if (!mediaHasStemMix(media) || !mix) return;
    const context = createAudioContext();
    if (!context) return;

    const musicElement = createStemElement(media.instrumentalStemUrl);
    const vocalsElement = createStemElement(media.vocalStemUrl);
    try {
      const musicSource = context.createMediaElementSource(musicElement);
      const vocalsSource = context.createMediaElementSource(vocalsElement);
      const musicGain = context.createGain();
      const vocalsGain = context.createGain();
      const masterGain = context.createGain();

      musicSource.connect(musicGain).connect(masterGain);
      vocalsSource.connect(vocalsGain).connect(masterGain);
      masterGain.connect(context.destination);
      graphRef.current = {
        context,
        musicElement,
        vocalsElement,
        musicGain,
        vocalsGain,
        masterGain,
      };
    } catch {
      void context.close().catch(() => {});
      return;
    }

    const preview = previewRef.current;
    if (preview) {
      syncElement(musicElement, preview);
      syncElement(vocalsElement, preview);
    }

    return () => {
      graphRef.current = null;
      musicElement.pause();
      vocalsElement.pause();
      musicElement.removeAttribute("src");
      vocalsElement.removeAttribute("src");
      musicElement.load();
      vocalsElement.load();
      void context.close().catch(() => {});
    };
  }, [media, mixActive, previewRef, stemKey]);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph || !mix) return;
    graph.musicGain.gain.value = mix.musicVolume;
    graph.vocalsGain.gain.value = mix.vocalsVolume;
  }, [mix]);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;
    graph.masterGain.gain.value = muted ? 0 : clampAudioMixVolume(volume);
  }, [muted, volume]);

  // `mixActive` is a dep so this re-runs once the setup effect has built the graph.
  // The mix only becomes active after `loadedmetadata` (doc.audioMix is set then),
  // which is *after* this effect first mounts — without the dep the graph would
  // exist but its play/pause listeners would never be attached, so the stems would
  // never start (silent mix that ignores the volume sliders).
  useEffect(() => {
    const preview = previewRef.current;
    const graph = graphRef.current;
    if (!preview || !graph) return;

    const syncStems = () => {
      syncElement(graph.musicElement, preview);
      syncElement(graph.vocalsElement, preview);
    };
    const playStems = () => {
      syncStems();
      if (graph.context.state === "suspended") void graph.context.resume().catch(() => {});
      void graph.musicElement.play().catch(() => {});
      void graph.vocalsElement.play().catch(() => {});
    };
    const pauseStems = () => {
      graph.musicElement.pause();
      graph.vocalsElement.pause();
    };
    const onRateChange = () => {
      graph.musicElement.playbackRate = preview.playbackRate;
      graph.vocalsElement.playbackRate = preview.playbackRate;
    };

    preview.addEventListener("play", playStems);
    preview.addEventListener("pause", pauseStems);
    preview.addEventListener("ended", pauseStems);
    preview.addEventListener("seeking", syncStems);
    preview.addEventListener("seeked", syncStems);
    preview.addEventListener("ratechange", onRateChange);

    let rafId = 0;
    const driftGuard = () => {
      if (!preview.paused) {
        const current = preview.currentTime;
        if (Math.abs(graph.musicElement.currentTime - current) > 0.08) {
          graph.musicElement.currentTime = current;
        }
        if (Math.abs(graph.vocalsElement.currentTime - current) > 0.08) {
          graph.vocalsElement.currentTime = current;
        }
      }
      rafId = requestAnimationFrame(driftGuard);
    };
    driftGuard();
    if (!preview.paused) playStems();

    return () => {
      preview.removeEventListener("play", playStems);
      preview.removeEventListener("pause", pauseStems);
      preview.removeEventListener("ended", pauseStems);
      preview.removeEventListener("seeking", syncStems);
      preview.removeEventListener("seeked", syncStems);
      preview.removeEventListener("ratechange", onRateChange);
      cancelAnimationFrame(rafId);
      pauseStems();
    };
  }, [previewRef, stemKey, mixActive]);
}
